import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Tooltip, Select as AntSelect, Row, Col } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGridHorizontal } from '@fortawesome/pro-solid-svg-icons/faGridHorizontal'
import { faCog } from '@fortawesome/pro-solid-svg-icons/faCog'
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { faAdd } from '@fortawesome/pro-solid-svg-icons/faAdd'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { ICollection, IRelatedCourse } from 'interfaces/learningPath'
import { useAppDispatch } from 'store/hooks'
import { slices } from 'store/slices'
import EmptyState from 'components/reusables/emptyState'
import Select from 'components/reusables/select'
import Prerequisites from 'components/reusables/prerequisites'
import { CourseStateType } from 'enum/course-state.enum'
import { CourseLmsProfileType } from 'enum/course-lms-profile-type.enum'
import { CourseStateBadge } from 'components/reusables/states'
import AddCourses from './addCourses'
import {
  Options,
  DragAndDrop,
  AddCourse,
  Column,
  TooltipTitle,
  TableHeader,
  TableBody,
  TotalCourses,
  EmptyStateWrapper,
  CourseName,
} from './styled'
import AddPrerequisites from './addPrerequisites'

const { Option } = AntSelect

interface IProps {
  collection: ICollection
}

const Courses = ({ collection }: IProps) => {
  const { t } = useTranslation()
  const prefix =
    'administration.learningPaths.manage.create.content.collections.courses'
  const dispatch = useAppDispatch()
  const [isAdding, setIsAdding] = useState(false)
  const [isAddingPrerequisites, setIsAddingPrerequisites] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState<IRelatedCourse | null>(
    null
  )

  const onAddPrerequisites = (course: IRelatedCourse) => {
    setSelectedCourse(course)
    setIsAddingPrerequisites(true)
  }

  const onDragEnd = (result: any) => {
    const { destination, source } = result

    if (!destination || destination.index === source.index) {
      return
    }

    const newCourses = reorder(
      collection.relatedCourses,
      source.index,
      destination.index
    )
    dispatch(
      slices.learningPath.updateRelatedCoursesOrder({
        collectionOrder: collection.order,
        relatedCourses: newCourses,
      })
    )
  }

  const reorder = (
    list: IRelatedCourse[],
    startIndex: number,
    endIndex: number
  ) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }

  return (
    <>
      <TotalCourses>
        <span>
          <FontAwesomeIcon icon={faBook} />
          {collection.relatedCourses.length}{' '}
          {collection.relatedCourses.length === 1
            ? t(`${prefix}.rowsCount`)
            : t(`${prefix}.rowsCountPlural`)}
        </span>
      </TotalCourses>
      <TableHeader>
        <Row type="flex">
          <Col style={{ width: 40 }} />
          <Col style={{ flex: 1, paddingLeft: 20 }}>
            {t(`${prefix}.columns.courses`)}
          </Col>
          <Col style={{ width: 245 }}>
            <Column>
              {t(`${prefix}.columns.prerequisites.title`)}
              <Tooltip
                title={
                  <TooltipTitle>
                    {t(`${prefix}.columns.prerequisites.tooltip`)}
                  </TooltipTitle>
                }
                placement="top"
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </Column>
          </Col>
          <Col style={{ width: 150 }}>{t(`${prefix}.columns.state`)}</Col>
          <Col style={{ width: 139 }}>
            <Column>
              {t(`${prefix}.columns.required.title`)}
              <Tooltip
                title={
                  <TooltipTitle>
                    {t(`${prefix}.columns.required.description`)}
                    <a
                      href="https://rankmi.odoo.com/knowledge/article#id=2622&cids=1"
                      target="_blank"
                    >
                      {t(`${prefix}.columns.required.descriptionSuffix`)}
                    </a>
                  </TooltipTitle>
                }
                placement="top"
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </Column>
          </Col>
          <Col style={{ width: 85 }} />
        </Row>
      </TableHeader>
      <TableBody>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {collection.relatedCourses.map((relatedCourse, index) => (
                  <Draggable
                    key={index}
                    draggableId={index.toString()}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{ ...provided.draggableProps.style }}
                      >
                        <Row
                          key={index}
                          type="flex"
                          style={{
                            backgroundColor:
                              relatedCourse.course.state ===
                                CourseStateType.Draft ||
                              relatedCourse.course.state ===
                                CourseStateType.Inactive
                                ? '#FFF9E3'
                                : 'white',
                          }}
                        >
                          <Col style={{ width: 40 }}>
                            <DragAndDrop>
                              <Button type="link" {...provided.dragHandleProps}>
                                <FontAwesomeIcon icon={faGridHorizontal} />
                              </Button>
                            </DragAndDrop>
                          </Col>
                          <Col style={{ flex: 1, display: 'grid' }}>
                            <CourseName>
                              {(relatedCourse.course.state ===
                                CourseStateType.Draft ||
                                relatedCourse.course.state ===
                                  CourseStateType.Inactive) && (
                                <Tooltip
                                  title={
                                    <TooltipTitle>
                                      {t(
                                        relatedCourse.course.state ===
                                          CourseStateType.Inactive
                                          ? `${prefix}.columns.coursesV2.tooltipInactive`
                                          : `${prefix}.columns.coursesV2.tooltipDraft`
                                      )}
                                    </TooltipTitle>
                                  }
                                  placement="bottom"
                                >
                                  <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                  />
                                </Tooltip>
                              )}
                              {relatedCourse.course.name}
                            </CourseName>
                          </Col>
                          <Col style={{ width: 245 }}>
                            <Prerequisites
                              items={relatedCourse.prerequisites.map(
                                (item) => item.name
                              )}
                            />
                          </Col>
                          <Col style={{ width: 150 }}>
                            <>
                              {relatedCourse.course ? (
                                <CourseStateBadge
                                  state={relatedCourse.course.state}
                                />
                              ) : (
                                '--'
                              )}
                            </>
                          </Col>
                          <Col style={{ width: 139 }}>
                            <Tooltip
                              title={
                                relatedCourse.prerequisites.length > 0 && (
                                  <TooltipTitle>
                                    {t(`${prefix}.columns.required.tooltip`)}
                                  </TooltipTitle>
                                )
                              }
                              placement="left"
                            >
                              <div>
                                <Select
                                  value={relatedCourse.required}
                                  onSelect={(value: number) =>
                                    dispatch(
                                      slices.learningPath.changeObligation({
                                        required: value,
                                        courseOrder: relatedCourse.order,
                                        collectionOrder: collection.order,
                                      })
                                    )
                                  }
                                  disabled={
                                    relatedCourse.prerequisites.length > 0
                                  }
                                >
                                  <Option
                                    key={CourseLmsProfileType.Required}
                                    value={CourseLmsProfileType.Required}
                                  >
                                    {t(
                                      `${prefix}.columns.required.values.required`
                                    )}
                                  </Option>
                                  <Option
                                    key={CourseLmsProfileType.Optional}
                                    value={CourseLmsProfileType.Optional}
                                  >
                                    {t(
                                      `${prefix}.columns.required.values.optional`
                                    )}
                                  </Option>
                                </Select>
                              </div>
                            </Tooltip>
                          </Col>
                          <Col style={{ width: 85 }}>
                            <Options>
                              <Tooltip
                                title={t(`${prefix}.columns.options.remove`)}
                                placement="right"
                              >
                                <Button
                                  onClick={() =>
                                    dispatch(
                                      slices.learningPath.removeCourse({
                                        courseOrder: relatedCourse.order,
                                        collectionOrder: collection.order,
                                      })
                                    )
                                  }
                                  type="link"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              </Tooltip>
                              <Tooltip
                                title={t(`${prefix}.columns.options.settings`)}
                                placement="right"
                              >
                                <Button
                                  onClick={() =>
                                    onAddPrerequisites(relatedCourse)
                                  }
                                  type="link"
                                >
                                  <FontAwesomeIcon icon={faCog} />
                                </Button>
                              </Tooltip>
                            </Options>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </TableBody>
      {collection.relatedCourses.length === 0 && (
        <EmptyStateWrapper>
          <EmptyState
            title={t(`${prefix}.emptyState.title`)}
            icon={faBook}
            action={() => setIsAdding(true)}
            actionIcon={faAdd}
            actionTitle={t(`${prefix}.emptyState.actionTitle`)}
          />
        </EmptyStateWrapper>
      )}
      {collection.relatedCourses.length > 0 && (
        <AddCourse>
          <Button type="link" onClick={() => setIsAdding(true)}>
            <FontAwesomeIcon icon={faAdd} />
            {t(`${prefix}.buttons.addCourse`)}
          </Button>
        </AddCourse>
      )}
      {isAdding && (
        <AddCourses
          collectionOrder={collection.order}
          close={() => setIsAdding(false)}
        />
      )}
      {isAddingPrerequisites && selectedCourse && (
        <AddPrerequisites
          collectionOrder={collection.order}
          relatedCourse={selectedCourse}
          close={() => setIsAddingPrerequisites(false)}
        />
      )}
    </>
  )
}

export default Courses
